/**
 * Installs global axios + fetch interceptors that rewrite URLs which point at
 * the build-time `REACT_APP_BACKEND_URL` to the current page origin. Keeps all
 * /api/* calls same-origin on production custom domains, which eliminates the
 * third-party cookie problem.
 *
 * Imported once at the top of `src/index.js` — runs its side effects on first
 * import.
 */
import axios from 'axios';
import { ENV_BACKEND_URL, getRuntimeApiBase, rewriteApiUrl } from './apiBase';

// ── axios ──────────────────────────────────────────────────────────────────
// Always send cookies on same-origin + cross-origin XHRs.
axios.defaults.withCredentials = true;

axios.interceptors.request.use((config) => {
  try {
    if (config.url) config.url = rewriteApiUrl(config.url);
    if (config.baseURL) config.baseURL = rewriteApiUrl(config.baseURL);
  } catch (_) {
    // never break a request because of URL rewriting
  }
  return config;
});

// ── Global 401 handler for /api/admin/* ────────────────────────────────────
// Any authenticated admin request that returns 401 (cookie expired, server
// invalidated the session, logged out from another tab) must:
//   1. Purge the stale localStorage.admin_user marker.
//   2. Broadcast a `storage` event so other open tabs redirect themselves.
//   3. Redirect this tab to the login screen (unless we're already there or
//      the request WAS the login/logout endpoint itself — those handle 401
//      inline via error messages).
// The `/api/admin/me` probe is intentionally allowed to 401 silently — that's
// how AdminRoute + AdminLogin detect a dead session; we only cleanse
// localStorage and let the guards handle navigation.
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const url = err?.config?.url || '';
      const status = err?.response?.status;
      if (status === 401 && typeof url === 'string' && url.includes('/api/admin/')) {
        const isAuthEndpoint =
          url.includes('/api/admin/login') || url.includes('/api/admin/logout');
        if (!isAuthEndpoint) {
          const hadMarker = localStorage.getItem('admin_user');
          if (hadMarker) localStorage.removeItem('admin_user');
          const isMeProbe = url.includes('/api/admin/me');
          if (!isMeProbe &&
              typeof window !== 'undefined' &&
              !window.location.pathname.startsWith('/admin/login')) {
            window.location.replace('/admin/login');
          }
        }
      }
    } catch (_) {
      // never mask the original error
    }
    return Promise.reject(err);
  },
);

// ── Cross-tab session sync ─────────────────────────────────────────────────
// If another tab clears `admin_user` (logout / interceptor purge above),
// this tab should redirect itself to the login screen if it's still sitting
// on any /admin/* route besides /admin/login itself.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== 'admin_user') return;
    if (e.newValue !== null) return; // only react to deletions
    const path = window.location.pathname || '';
    if (path.startsWith('/admin') && !path.startsWith('/admin/login')) {
      window.location.replace('/admin/login');
    }
  });
}

// ── window.fetch ───────────────────────────────────────────────────────────
// sendBeacon analytics + any direct fetch() calls pick up the rewrite for
// free via this wrapper. We also force `credentials: 'include'` so auth
// cookies always ride along.
if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    try {
      if (typeof input === 'string') {
        input = rewriteApiUrl(input);
      } else if (input && typeof input.url === 'string') {
        const newUrl = rewriteApiUrl(input.url);
        if (newUrl !== input.url) {
          input = new Request(newUrl, input);
        }
      }
      if (!init) init = {};
      if (init.credentials === undefined) init.credentials = 'include';
    } catch (_) {
      // best-effort — fall through to the native call
    }
    return originalFetch(input, init);
  };
}

// ── Debug aid ──────────────────────────────────────────────────────────────
// Expose the resolved base URL in the browser devtools as a sanity check.
// Remove after prod stabilises if desired.
if (typeof window !== 'undefined') {
  window.__ACF_API_BASE__ = getRuntimeApiBase();
  window.__ACF_ENV_API_BASE__ = ENV_BACKEND_URL;
}

console.info('[ACF] API base resolved:', getRuntimeApiBase(), '(env:', ENV_BACKEND_URL || 'none', ')');
